import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Package, Shield, FileText, CheckCircle, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { services, whyChooseUs } from '../data/mock';
import { useSiteContent } from '../hooks/useSiteContent';
import { useSEO } from '../hooks/useSEO';

/**
 * Zion Underwriting Partners tenant home page, rendered at /tenant-home.
 *
 * Every visible string + URL is sourced from the ``tenant_home`` CMS key
 * via ``useSiteContent`` (Session 213.83), with the original hardcoded
 * copy retained as fallback defaults so the page renders correctly even
 * before any CMS save.
 */
const Home = () => {
  // Session 215.81/215.84 — customer-facing landing at "/". GSC
  // targeting for "trucking insurance", "wholesale trucking
  // insurance", and "transportation insurance" (see also the
  // per-service body copy in the Services section below).
  useSEO({
    title:
      'Trucking Insurance & Wholesale Transportation Insurance | Zion Underwriting Partners',
    description:
      'Wholesale trucking insurance and transportation insurance from A-rated carriers. Zion Underwriting Partners specializes in commercial trucking insurance for owner-operators, small fleets, and program administrators — APD, Motor Truck Cargo, Non-Trucking Liability, and Trucker\'s General Liability.',
    path: '/',
  });
  const navigate = useNavigate();

  // CMS — every visible string. Hardcoded values here are pure
  // fallbacks; saved CMS values override. The default copy is
  // keyword-weighted for GSC (see Session 215.84): "trucking
  // insurance", "wholesale trucking insurance", and "transportation
  // insurance" each appear at least once in the body copy without
  // sacrificing readability.
  const cms = useSiteContent('tenant_home', {
    hero_title_lead:      'Wholesale Trucking Insurance &',
    hero_title_highlight: 'Transportation Insurance',
    hero_subtitle:        'Zion Underwriting Partners is your trusted wholesale trucking insurance and transportation insurance partner — auto physical damage, motor truck cargo, non-trucking liability, and trucker\'s general liability coverage from A-rated carriers.',
    hero_cta_primary_label:   'Get a Quote',
    hero_cta_primary_href:    '/quote',
    hero_cta_secondary_label: 'Contact Us',
    hero_bg_image_url:        'https://images.unsplash.com/photo-1574757974346-45bae947d89a',

    services_heading:  'Wholesale Trucking Insurance Coverages',
    services_subtitle: 'Comprehensive commercial trucking insurance and transportation insurance solutions tailored to your business needs',

    about_heading:    'About Zion Underwriting Partners',
    about_paragraph_1: 'Zion Underwriting Partners LLC is a specialized wholesale trucking insurance and transportation insurance provider based in the United States. We deliver comprehensive commercial trucking insurance solutions for the trucking and transportation industry.',
    about_paragraph_2: 'With years of experience and strong relationships with A-rated insurance carriers, we deliver competitive wholesale trucking insurance rates, fast turnaround times, and dedicated support to help your business thrive.',
    about_paragraph_3: 'Our transportation insurance expertise spans auto physical damage, motor truck cargo, non-trucking liability, and trucker\'s general liability. We understand the unique challenges of the trucking industry and provide tailored coverage to meet your specific needs — whether you\'re an owner-operator, small fleet, or program administrator.',
    about_cta_label:  'Request a Quote',
    about_cta_href:   '/quote',
    about_image_url:  'https://images.unsplash.com/photo-1492168732976-2676c584c675',

    why_heading:  'Why Choose Zion Underwriting Partners?',
    why_subtitle: 'Wholesale trucking insurance expertise and exceptional service to protect your transportation business',

    cta_heading:              'Ready to Get Started?',
    cta_subtitle:             'Contact us today for a wholesale trucking insurance quote or to learn more about our transportation insurance solutions.',
    cta_primary_label:        'Get a Quote',
    cta_primary_href:         '/quote',
    cta_secondary_label:      'Contact Us',
    cta_background_image_url: 'https://images.unsplash.com/photo-1638262052640-82e94d64664a',

    contact_heading:  'Get In Touch',
    contact_subtitle: 'Questions about trucking insurance? We\'re here to help. Reach out to us today.',
    contact_phone:        '(555) 123-4567',
    contact_phone_href:   'tel:5551234567',
    contact_email:        'info@zionuw.com',
    contact_email_href:   'mailto:info@zionuw.com',
    contact_address_line_1: '123 Insurance Way, Suite 100',
    contact_address_line_2: 'City, State 12345',

    form_heading:      'Send Us a Message',
    form_subtitle:     'Fill out the form below and we\'ll get back to you as soon as possible.',
    form_submit_label: 'Send Message',
    form_success_text: 'Thank you for contacting us! We\'ll get back to you soon.',
  });

  const iconMap = { Truck, Package, Shield, FileText };

  // Hero CTA secondary button — if href looks like a path navigate,
  // otherwise scroll to #contact (preserves original behavior).
  const onSecondaryHeroCta = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-[600px] flex items-center justify-center bg-gradient-to-br from-[#1e3a5f] via-[#2a4a6f] to-[#1e3a5f] text-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {cms.hero_bg_image_url && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${cms.hero_bg_image_url})` }}
          ></div>
        )}

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {cms.hero_title_lead}
              <span className="block text-[#D1D5DB] mt-2">{cms.hero_title_highlight}</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              {cms.hero_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={() => navigate(cms.hero_cta_primary_href || '/quote')}
                className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                data-testid="tenant-home-cta-primary"
              >
                {cms.hero_cta_primary_label}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onSecondaryHeroCta}
                className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] text-lg px-8 py-6 transition-all"
                data-testid="tenant-home-cta-secondary"
              >
                {cms.hero_cta_secondary_label}
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">{cms.services_heading}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{cms.services_subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 border-gray-200">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-[#9CA3AF] to-[#6B7280] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      {IconComponent && <IconComponent className="h-7 w-7 text-white" />}
                    </div>
                    <CardTitle className="text-xl text-[#1e3a5f]">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-6">{cms.about_heading}</h2>
              <p className="text-gray-600 mb-6 leading-relaxed text-lg">{cms.about_paragraph_1}</p>
              <p className="text-gray-600 mb-6 leading-relaxed">{cms.about_paragraph_2}</p>
              <p className="text-gray-600 mb-8 leading-relaxed">{cms.about_paragraph_3}</p>
              <Button
                onClick={() => navigate(cms.about_cta_href || '/quote')}
                className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white px-8 py-6 text-lg"
                data-testid="tenant-home-about-cta"
              >
                {cms.about_cta_label}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                {cms.about_image_url && (
                  <img src={cms.about_image_url} alt="Truck fleet" className="w-full h-[500px] object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/60 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#9CA3AF]/20 rounded-2xl -z-10"></div>
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#1e3a5f]/10 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">{cms.why_heading}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{cms.why_subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item) => (
              <div key={item.id} className="flex gap-4 p-6 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-[#9CA3AF]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2a4a6f] text-white relative overflow-hidden">
        {cms.cta_background_image_url && (
          <div
            className="absolute inset-0 opacity-10 bg-cover bg-center"
            style={{ backgroundImage: `url(${cms.cta_background_image_url})` }}
          ></div>
        )}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">{cms.cta_heading}</h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">{cms.cta_subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate(cms.cta_primary_href || '/quote')}
                className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white text-lg px-8 py-6"
                data-testid="tenant-home-cta-band-primary"
              >
                {cms.cta_primary_label}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onSecondaryHeroCta}
                className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] text-lg px-8 py-6"
                data-testid="tenant-home-cta-band-secondary"
              >
                {cms.cta_secondary_label}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">{cms.contact_heading}</h2>
              <p className="text-lg text-gray-600">{cms.contact_subtitle}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-[#9CA3AF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-6 w-6 text-[#6B7280]" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">Phone</h3>
                  <a href={cms.contact_phone_href || `tel:${(cms.contact_phone || '').replace(/\D/g, '')}`} className="text-gray-600 hover:text-[#6B7280] transition-colors">
                    {cms.contact_phone}
                  </a>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-[#9CA3AF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-6 w-6 text-[#6B7280]" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">Email</h3>
                  <a href={cms.contact_email_href || `mailto:${cms.contact_email}`} className="text-gray-600 hover:text-[#6B7280] transition-colors">
                    {cms.contact_email}
                  </a>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-[#9CA3AF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-6 w-6 text-[#6B7280]" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">Location</h3>
                  <p className="text-gray-600 text-sm">
                    {cms.contact_address_line_1}<br />{cms.contact_address_line_2}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#1e3a5f]">{cms.form_heading}</CardTitle>
                <CardDescription>{cms.form_subtitle}</CardDescription>
              </CardHeader>
              <CardContent>
                <ContactForm submitLabel={cms.form_submit_label} successText={cms.form_success_text} />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Form Component
const ContactForm = ({ submitLabel = 'Send Message', successText = 'Thank you for contacting us! We\'ll get back to you soon.' }) => {
  const [formData, setFormData] = React.useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    }, 1000);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
          <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9CA3AF] focus:border-transparent transition-colors"
            placeholder="John Doe" />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
          <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9CA3AF] focus:border-transparent transition-colors"
            placeholder="john@example.com" />
        </div>
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d9b9b] focus:border-transparent transition-colors"
          placeholder="(555) 123-4567" />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
        <textarea id="message" name="message" required value={formData.message} onChange={handleChange} rows={5}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9CA3AF] focus:border-transparent transition-colors resize-none"
          placeholder="Tell us how we can help you..." ></textarea>
      </div>

      {submitStatus === 'success' && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successText}
        </div>
      )}

      <Button type="submit" disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white py-6 text-lg">
        {isSubmitting ? 'Sending...' : submitLabel}
      </Button>
    </form>
  );
};

export default Home;
